
const ipModal = {
    _modal: null,
    _content: null,
    _originalParent: null,
    _originalNextSibling: null,
    _focusableElements: [],
    _lastActiveElement: null, // This will store our trigger element

    /**
     * Initializes the modal listeners for declarative activation.
     */
    init() {
        // Listen for clicks on any element that might trigger a modal
        document.addEventListener('click', (e) => {
            // Trigger via <a href="#modalId">
            const link = e.target.closest('a[href^="#"]');
            if (link) {
                const targetId = link.getAttribute('href');
                try {
                    const targetElement = document.querySelector(targetId);
                    if (targetElement && targetElement.classList.contains('ipModal-window-content')) {
                        e.preventDefault();
                        this.show(targetElement);
                        return; // Stop further processing
                    }
                } catch (err) {
                    console.warn('ipModal: Invalid selector in href for modal trigger.', err);
                }
            }

            // Trigger via <button aria-controls="modalId">
            const trigger = e.target.closest('[aria-controls]');
            if (trigger) {
                const targetId = trigger.getAttribute('aria-controls');
                try {
                    const targetElement = document.getElementById(targetId);
                    if (targetElement && targetElement.classList.contains('ipModal-window-content')) {
                        e.preventDefault();
                        this.show(targetElement);
                    }
                } catch (err) {
                    console.warn('ipModal: Invalid ID in aria-controls for modal trigger.', err);
                }
            }
        });

        // Bind the 'this' context for the Escape key handler once
        this._handleEscKey = this._handleEscKey.bind(this);
    },

    /**
     * Shows the modal window.
     * @param {string|HTMLElement} source - A CSS selector, the content element, or a trigger element.
     */
    show(source) {
        if (this._modal) {
            console.warn('ipModal is already active.');
            return;
        }

        let contentElement;

        // Determine the content element based on the source type
        if (typeof source === 'string') {
            contentElement = document.querySelector(source);
        } else if (source instanceof HTMLElement) {
            if (source.classList.contains('ipModal-window-content')) {
                contentElement = source; // Direct element reference
            } else { // Trigger element like a button (`onclick="ipModal.show(this)"`)
                const wrapper = source.closest('.ipModal');
                contentElement = wrapper ? wrapper.querySelector('.ipModal-window-content') : null;
            }
        }

        if (!contentElement) {
            console.error('ipModal: Content element not found for source:', source);
            return;
        }

        // Create the 'beforeshow' event
        const beforeShowEvent = new CustomEvent('ipmodal:beforeshow', {
            bubbles: true,
            cancelable: true,
            detail: { content: contentElement }
        });

        // Execute data-attribute handler for beforeshow
        const beforeShowHandlerName = contentElement.dataset.onBeforeshow;
        if (beforeShowHandlerName && typeof window[beforeShowHandlerName] === 'function') {
            window[beforeShowHandlerName](beforeShowEvent);
        }

        // Dispatch event for other listeners
        contentElement.dispatchEvent(beforeShowEvent);

        // Check if any handler (data-attribute or listener) cancelled the event
        if (beforeShowEvent.defaultPrevented) {
            return;
        }

        this._content = contentElement;

        // Store original position and the trigger element
        this._originalParent = this._content.parentNode;
        this._originalNextSibling = this._content.nextSibling;
        this._lastActiveElement = document.activeElement;

        // Set aria-expanded on the trigger element
        if (this._lastActiveElement) {
            this._lastActiveElement.setAttribute('aria-expanded', 'true');
        }

        // Create the modal DOM structure
        this._modal = document.createElement('div');
        this._modal.className = 'ipModal-container';
        this._modal.innerHTML = `
      <div class="ipModal-overlay"></div>
      <div class="ipModal-window"></div>
    `;

        const modalWindow = this._modal.querySelector('.ipModal-window');

        // Explicitly ensure accessibility attributes are set
        if (modalWindow.getAttribute('role') !== 'dialog') {
            modalWindow.setAttribute('role', 'dialog');
        }
        if (modalWindow.getAttribute('aria-modal') !== 'true') {
            modalWindow.setAttribute('aria-modal', 'true');
        }

        // Check if the modal can be self-closed by the user
        const canSelfClose = contentElement.dataset.selfClose !== 'false';

        // Show close button only if enabled and self-closing is allowed
        if (contentElement.dataset.showClose === 'true' && canSelfClose) {
            const closeButton = document.createElement('button');
            closeButton.className = 'ipModal-close-btn';
            closeButton.setAttribute('aria-label', 'Close modal');
            closeButton.innerHTML = '&times;';
            closeButton.addEventListener('click', () => this.close());
            // Prepend the button so it's the first element for focus trapping
            modalWindow.prepend(closeButton);
        }

        modalWindow.appendChild(this._content);
        document.body.prepend(this._modal);
        document.body.classList.add('ipModal-active');

        // Add listeners for closing only if self-closing is allowed
        if (canSelfClose) {
            this._modal.querySelector('.ipModal-overlay').addEventListener('click', () => this.close());
            document.addEventListener('keydown', this._handleEscKey);
        }

        this._trapFocus();

        // Create the 'onshow' event
        const onShowEvent = new CustomEvent('ipmodal:onshow', {
            bubbles: true,
            detail: { modal: this._modal, content: this._content }
        });

        // Execute data-attribute handler for onshow
        const onShowHandlerName = this._content.dataset.onShow;
        if (onShowHandlerName && typeof window[onShowHandlerName] === 'function') {
            window[onShowHandlerName](onShowEvent);
        }

        // Dispatch event for other listeners
        this._content.dispatchEvent(onShowEvent);
    },

    /**
     * Closes the currently active modal window.
     */
    close() {
        if (!this._modal || !this._content) return;

        const closedContentElement = this._content;

        if (this._originalParent) {
            this._originalParent.insertBefore(this._content, this._originalNextSibling);
        } else {
            document.body.appendChild(this._content);
        }

        this._modal.remove();
        document.body.classList.remove('ipModal-active');
        // Always try to remove the listener, it won't error if it's not there
        document.removeEventListener('keydown', this._handleEscKey);

        this._untrapFocus();

        // Set aria-expanded back to false and return focus
        if (this._lastActiveElement) {
            this._lastActiveElement.setAttribute('aria-expanded', 'false');
            this._lastActiveElement.focus();
        }

        // Reset all state properties
        this._modal = null;
        this._content = null;
        this._originalParent = null;
        this._originalNextSibling = null;
        this._lastActiveElement = null;

        // Create the 'onclose' event
        const onCloseEvent = new CustomEvent('ipmodal:onclose', {
            bubbles: true,
            detail: { content: closedContentElement }
        });

        // Execute data-attribute handler for onclose
        const onCloseHandlerName = closedContentElement.dataset.onClose;
        if (onCloseHandlerName && typeof window[onCloseHandlerName] === 'function') {
            window[onCloseHandlerName](onCloseEvent);
        }

        // Dispatch event for other listeners
        closedContentElement.dispatchEvent(onCloseEvent);
    },

    /**
     * Handles the 'Escape' key press to close the modal.
     * @private
     */
    _handleEscKey(e) {
        if (e.key === 'Escape') {
            this.close();
        }
    },

    /**
     * Traps keyboard focus within the modal to maintain accessibility.
     * @private
     */
    _trapFocus() {
        // A more robust selector for all focusable elements
        const focusableSelector = 'a[href]:not([disabled]), button:not([disabled]), textarea:not([disabled]), input:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])';

        const focusableElementsOnPage = document.querySelectorAll(focusableSelector);
        this._focusableElements = [];

        focusableElementsOnPage.forEach(el => {
            // If the element is outside the modal, disable its focus
            if (!this._modal.contains(el)) {
                this._focusableElements.push({
                    element: el,
                    originalTabindex: el.getAttribute('tabindex')
                });
                el.setAttribute('tabindex', '-1');
            }
        });

        // Find the first focusable element within the modal
        const firstFocusableElement = this._modal.querySelector(focusableSelector);

        if (firstFocusableElement) {
            // If a focusable element is found, focus it
            firstFocusableElement.focus();
        } else {
            // If no focusable elements, focus the window itself for accessibility
            const modalWindow = this._modal.querySelector('.ipModal-window');
            modalWindow.setAttribute('tabindex', '-1');
            modalWindow.focus();
        }
    },

    /**
     * Restores keyboard focus to the document after the modal is closed.
     * @private
     */
    _untrapFocus() {
        this._focusableElements.forEach(({ element, originalTabindex }) => {
            if (originalTabindex === null) {
                element.removeAttribute('tabindex');
            } else {
                element.setAttribute('tabindex', originalTabindex);
            }
        });
        this._focusableElements = [];
    }
};

export default ipModal;
